// frontend/packages/business-console/src/components/media/MediaDetailsDrawer.jsx

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Drawer,
  Stack,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CircularProgress from '@mui/material/CircularProgress';

import { toCldToken } from '../../utils/mediaToken';
import { getMediaUsage } from '../../services/mediaService';

const safe = (v) => (v == null ? '' : String(v));

// What the save did to the site (alt text, title, caption and description are
// written to the site's preview draft; the tenant's Publish takes them live).
function siteSyncNote(siteSync) {
  if (!siteSync) return null;
  if (siteSync.synced) {
    return {
      severity: 'success',
      text: 'Saved to your site preview. It goes live the next time you publish.',
    };
  }
  if (siteSync.skipped === 'no_change') {
    return { severity: 'info', text: 'Saved. Your site preview already has this.' };
  }
  if (['no_site', 'nothing_syncable', 'no_business'].includes(siteSync.skipped)) {
    return null;
  }
  return {
    severity: 'warning',
    text: 'Saved, but your site preview could not be updated. Save again to retry.',
  };
}

export default function MediaDetailsDrawer({
  open,
  asset,
  onClose,
  onSave,
  onDelete,
}) {
  const url = asset?.publicUrl || asset?.url || asset?.cdnUrl || '';
  const type = String(asset?.type || 'image').toLowerCase();
  const id = asset?.id || asset?._id || '';

  const publicId = useMemo(() => {
    // In your backend mapping, blobPath is reused for Cloudinary public_id.
    return safe(asset?.blobPath);
  }, [asset?.blobPath]);

  const cldToken = useMemo(() => toCldToken(publicId), [publicId]);

  const [title, setTitle] = useState('');
  const [altText, setAltText] = useState('');
  const [caption, setCaption] = useState('');
  const [description, setDescription] = useState('');
  // Result of the last save's site sync (backend mediaSiteSyncService).
  const [syncNote, setSyncNote] = useState(null);
  const [folder, setFolder] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [error, setError] = useState('');

  // On-demand usage backref ("used on N pages"), fetched when the drawer
  // opens for an asset. Read-only; computed live by the content route.
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  useEffect(() => {
    setUsage(null);
    if (!open || !id) return undefined;
    let cancelled = false;
    setUsageLoading(true);
    getMediaUsage(id)
      .then((u) => {
        if (!cancelled) setUsage(u);
      })
      .catch(() => {
        if (!cancelled) setUsage(null);
      })
      .finally(() => {
        if (!cancelled) setUsageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, id]);

  const tags = useMemo(() => {
    return String(tagsText || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }, [tagsText]);

  useEffect(() => {
    setError('');
    setTitle(safe(asset?.title));
    setAltText(safe(asset?.altText));
    setCaption(safe(asset?.caption));
    setDescription(safe(asset?.description));
    setSyncNote(null);
    setFolder(safe(asset?.folder));
    setTagsText(Array.isArray(asset?.tags) ? asset.tags.join(', ') : '');
  }, [asset]);

  const altRequired = true; // global-only for now (your decision)

  const handleCopy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // ignore
    }
  };

  const canSave =
    Boolean(id) &&
    (!altRequired || type !== 'image' || altText.trim().length > 0);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: 340, sm: 420 }, p: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }} noWrap>
              Media Details
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }} noWrap>
              {type}
            </Typography>
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <Box
            sx={{
              width: '100%',
              aspectRatio: '4 / 3',
              bgcolor: 'action.hover',
              borderRadius: 1,
              overflow: 'hidden',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            {type === 'video' ? (
              <video
                src={url}
                controls
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <img
                src={url}
                alt={altText || ''}
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
          </Box>

          <Divider />

          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            size="small"
            fullWidth
          />

          {type === 'image' ? (
            <TextField
              label="Alt text"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              size="small"
              fullWidth
              required={altRequired}
              helperText={
                altRequired
                  ? 'Alt text is required before using this image.'
                  : ''
              }
            />
          ) : null}

          <TextField
            label="Caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            size="small"
            fullWidth
            inputProps={{ maxLength: 300 }}
            helperText="Shown under the photo on your site, e.g. the city or the job."
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            inputProps={{ maxLength: 1000 }}
            helperText="Longer notes about the photo. Used where your site has room."
          />

          <TextField
            label="Folder"
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
            size="small"
            fullWidth
            helperText="Examples: brand/ , blog/ , services/ , team/"
          />

          <TextField
            label="Tags"
            value={tagsText}
            onChange={(e) => setTagsText(e.target.value)}
            size="small"
            fullWidth
            helperText="Comma-separated"
          />

          <Divider />

          <Stack spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => handleCopy(cldToken)}
              disabled={!publicId}
              sx={{ fontWeight: 900 }}
            >
              Copy cld: token
            </Button>

            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => handleCopy(url)}
              disabled={!url}
              sx={{ fontWeight: 900 }}
            >
              Copy URL
            </Button>
          </Stack>

          <Divider />

          {/* Usage backref: where this asset is referenced in content. */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '.4px',
                opacity: 0.6,
                display: 'block',
                mb: 0.75,
              }}
            >
              Used on
            </Typography>
            {usageLoading ? (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ opacity: 0.7 }}
              >
                <CircularProgress size={14} />
                <Typography variant="body2">Checking content...</Typography>
              </Stack>
            ) : usage == null ? (
              <Typography variant="body2" sx={{ opacity: 0.6 }}>
                Usage unavailable.
              </Typography>
            ) : usage.count === 0 ? (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Not referenced in any page yet.
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {usage.count} {usage.count === 1 ? 'page' : 'pages'}
                  {usage.truncated ? ' (partial scan)' : ''}
                </Typography>
                {usage.files.map((f) => (
                  <Stack
                    key={f.path}
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                  >
                    <InsertDriveFileOutlinedIcon
                      sx={{ fontSize: 15, opacity: 0.6 }}
                    />
                    <Typography variant="body2" noWrap title={f.path}>
                      {f.name}
                      {f.collection ? (
                        <Box component="span" sx={{ opacity: 0.55 }}>
                          {' '}
                          · {f.collection}
                        </Box>
                      ) : null}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>

          <Divider />

          {syncNote ? (
            <Alert severity={syncNote.severity}>{syncNote.text}</Alert>
          ) : null}

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!canSave}
              onClick={async () => {
                try {
                  setError('');
                  setSyncNote(null);
                  const saved = await onSave(id, {
                    title: title || '',
                    altText: altText || '',
                    caption: caption || '',
                    description: description || '',
                    folder: folder || '',
                    tags,
                  });
                  setSyncNote(siteSyncNote(saved?.siteSync));
                } catch (e) {
                  setError(e?.message || String(e));
                }
              }}
              sx={{ fontWeight: 900, flex: 1 }}
            >
              Save
            </Button>

            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              disabled={!id}
              onClick={async () => {
                try {
                  setError('');
                  await onDelete(id);
                } catch (e) {
                  setError(e?.message || String(e));
                }
              }}
              sx={{ fontWeight: 900 }}
            >
              Delete
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}
